import {computed, effect, inject, Injectable} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {DOCUMENT, Location, PlatformLocation} from '@angular/common';
import {filter, firstValueFrom, map, startWith} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import {RouteDataService} from './route-data.service';
import {TranslocoService} from '@jsverse/transloco';
import {getAllRouteData} from "../utils";
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  private readonly translocoService = inject(TranslocoService);
  private readonly routeDataService = inject(RouteDataService);
  private readonly router = inject(Router);
  private readonly req = inject(PlatformLocation, { optional: true });
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly doc = inject(DOCUMENT);
  private readonly location = inject(Location);


  private initialUrl = this.req ? this.req?.pathname : document.URL;

  public enUrl = computed(() => {
    if (this.language() === "en") {
      return this.currentUrl();
    } else {
      return this.currentUrl().replace('/es', '/').replace('//', '/');
    }
  });

  public esUrl = computed(() => {
    if (this.language() === "es") {
      return this.currentUrl();
    }
    else {
      return this.currentUrl().startsWith('/es') ? this.currentUrl() : '/es' + this.currentUrl();
    }
  });

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );
  public readonly language = computed(() => this.routeDataService.routeData().language ?? (this.initialUrl.includes('/es/') ? 'es' : 'en'));

  /** Language from route data, or fallback */

  constructor() {
    // Update HTML lang + Transloco active language reactively
    effect(() => {
      const lang = this.language();
      this.doc.documentElement.setAttribute('lang', lang);
      this.translocoService.setActiveLang(lang);
    });

    // Update <link rel="alternate"> when navigating
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.updateAlternateLinks());
  }

  private removeAlternateLinks() {
    this.doc
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach(el => el.remove());
  }

  private updateAlternateLinks() {
    const data = getAllRouteData(this.activatedRoute);

    if (!data?.canTranslate) {
      this.removeAlternateLinks();
      return;
    }

    const routeLang = data.language ?? 'en';
    const baseUrl = environment.baseUrl;
    const path = this.location.path();

    // Generate hreflang URLs depending on current language
    const hrefs = this.buildAlternateUrls(baseUrl, path, routeLang);

    this.setAlternateLinks([
      { lang: 'en', href: hrefs.en },
      { lang: 'es', href: hrefs.es },
      { lang: 'x-default', href: hrefs.xDefault }
    ]);
  }

  private buildAlternateUrls(base: string, path: string, routeLang: string) {
    const cleanPath = path.replace(/^\/es/, '');

    return {
      en: base + cleanPath,
      es: base + '/es' + cleanPath,
      xDefault: base + (routeLang === 'es' ? '/es' + cleanPath : cleanPath)
    };
  }

  private setAlternateLinks(links: { lang: string; href: string }[]) {
    this.removeAlternateLinks(); // clear old ones first

    const head = this.doc.head;
    for (const { lang, href } of links) {
      const link = this.doc.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', href);
      head.appendChild(link);
    }
  }

  public async waitUntilLanguageLoaded(): Promise<void> {
    await this.translocoService.setActiveLang(this.language())
     await firstValueFrom(this.translocoService.load(this.language()));
  }
}
